import React from 'react';
import { Toggle } from '../Toggle';
import {
  InputGroup,
  InputGroupInput,
  InputGroupAddon,
  InputGroupButton,
} from '../InputGroup';
import { Icon } from '../Icon';
import styles from './TableToolbar.module.css';

/**
 * Unified toolbar for use above a DataTable.
 *
 * Layout: optional filter toggle button on the LEFT, then a search input,
 * then optional custom action node on the right. Filters themselves no
 * longer live in the toolbar — they're rendered in a side <FilterDrawer>
 * that the toolbar's filter button toggles open / closed.
 *
 * 2.0 design system: the filter button is a <Toggle> + <Icon>, the search is
 * an <InputGroup>. All slots are optional — pass only what the consumer needs.
 *
 * Props:
 *   search             — { value, onChange, placeholder } or omit
 *   count              — { value: number, label: string } renders a chip next to the search
 *   leadingActions     — ReactNode rendered RIGHT after the count chip, left-grouped
 *   actions            — ReactNode (e.g. <Button>Export</Button>), always right-grouped
 *   onFilterToggle     — fired when the filter toggle button is clicked
 *   filterButtonActive — if true, the filter button renders in its pressed state
 *   showFilterButton   — default true; pass false to hide the filter toggle
 */
const TableToolbar = ({
  search,
  count,
  leadingActions,
  actions,
  onFilterToggle,
  filterButtonActive = false,
  showFilterButton = true,
  className = '',
}) => {
  const hasSearch = !!search;
  const hasLeadingActions = !!leadingActions;
  const hasActions = !!actions;
  const hasCount = count !== undefined && count !== null;

  // A row-count chip alone keeps the toolbar present (Story 14.B).
  if (!hasSearch && !hasActions && !hasLeadingActions && !showFilterButton && !hasCount) {
    return null;
  }

  return (
    <div className={`${styles.toolbar} ${className}`}>
      {showFilterButton && (
        <Toggle
          variant="default"
          pressed={filterButtonActive}
          onPressedChange={() => onFilterToggle?.()}
          aria-label="Filter items"
          title="Filter items"
        >
          <Icon name="filter" />
        </Toggle>
      )}

      {hasSearch && (
        <InputGroup className={styles.search}>
          <InputGroupAddon align="inline-start">
            <Icon name="search" />
          </InputGroupAddon>
          <InputGroupInput
            value={search.value ?? ''}
            placeholder={search.placeholder ?? 'Search...'}
            onChange={(e) => search.onChange?.(e.target.value)}
          />
          {search.value && (
            <InputGroupAddon align="inline-end">
              <InputGroupButton
                variant="ghost"
                size="icon-xs"
                aria-label="Clear search"
                onClick={() => search.onChange?.('')}
              >
                <Icon name="close" />
              </InputGroupButton>
            </InputGroupAddon>
          )}
        </InputGroup>
      )}

      {hasCount && (
        <div className={styles.countChip}>
          <span className={styles.countValue}>{count.value}</span>
          <span className={styles.countLabel}>{count.label}</span>
        </div>
      )}

      {hasLeadingActions && (
        <div className={styles.leadingActionsSlot}>{leadingActions}</div>
      )}

      {hasActions && (
        <div className={styles.rightSection}>
          <div className={styles.actionsSlot}>{actions}</div>
        </div>
      )}
    </div>
  );
};

export default TableToolbar;
